import Vue from 'vue'
import Router from 'vue-router'
import Home from '@/components/Home'
import { Login, Logout, NotAuthorized, ForgotPassword, ResetPassword } from '@/components/authentication'
import { UsersList, UserEdit, MyProfile } from '@/components/users'
import { StaffList, StaffEdit } from '@/components/staff'
import { ClientsList, ClientEdit } from '@/components/clients'
import { BusinessEntitiesList, BusinessEntityEdit } from '@/components/businessEntities'
import { AssignmentsList, AssignmentEdit, PlacementGoals } from '@/components/assignments'
import { ConsultantTimeSheet } from '@/components/consultantTimeSheet'
import { TimeCardReport } from '@/components/timeCardReport'
import { DirectPlacementList, DirectPlacementEdit, DirectPlacementGoals } from '@/components/directPlacements'
import { CommissionReport, CommissionDetailsReport } from '@/components/commissionReports'
import addAuthentication from './addAuthentication'

import { InvoicingReport } from '@/components/invoicingReport'
import { PayrollReport } from '@/components/payrollReport'
import { GPToCostReport } from '@/components/gpToCostReport'
import { AdminDashboard } from '@/components/dashboard'
import { DealWizardContainer, DealsList } from '@/components/dealsV2'
import { TeamsList } from '@/components/teams'
import { ProjectsList, ProjectEdit } from '@/components/projects'
import { Settings } from '@/components/settings'
import { RevenueData } from '@/components/revenueData'
import { ClockLogs } from '@/components/clockLogs'
import { ClockLogsReport } from '@/components/clockLogsReport'
import { TimeManagement } from '@/components/timeManagement'
import { WorkflowTemplateList, WorkflowTemplateEdit } from '@/components/workflowTemplates'
import { WorkflowInstanceList, WorkflowInstanceDetail, MyWorkflowTasks, WorkflowDashboard } from '@/components/workflowInstances'
import { ImportRunsList, ImportRunDetail, ImportUpload } from '@/components/imports'
import { QuickBooksConnections } from '@/components/quickBooks'
import { InvoicesList, InvoiceDetail } from '@/components/invoices'
import { PlacementsList } from '@/components/placements'
import { DocumentTemplateList, DocumentTemplateEdit } from '@/components/documentTemplates'
import { WorkflowEmailTemplateList, WorkflowEmailTemplateEdit } from '@/components/workflowEmailTemplates'
import { WorkflowLibraryAssetList } from '@/components/workflowLibraryAssets'
import { WorkflowEmailReviewQueue } from '@/components/workflowEmailSends'

import {
  loginRoute,
  logoutRoute,
  defaultRoute,
  usersRoute,
  userCreateRoute,
  userEditRoute,
  staffRoute,
  staffCreateRoute,
  staffEditRoute,
  clientsRoute,
  clientCreateRoute,
  clientEditRoute,
  businessEntitiesRoute,
  businessEntityCreateRoute,
  businessEntityEditRoute,
  notAuthorizedRoute,
  assignmentsRoute,
  assignmentCreateRoute,
  assignmentEditRoute,
  consultantTimeSheetRoute,
  timeCardReportRoute,
  myProfileRoute,
  directPlacementsRoute,
  directPlacementCreateRoute,
  directPlacementEditRoute,
  commissionDetailsReportRoute,
  commissionReportRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  invoiceReportRoute,
  payrollReportRoute,
  gpToCostReportRoute,
  dashboardRoute,
  dealsRoute,
  dealWizardRoute,
  teamsRoute,
  projectsRoute,
  projectCreateRoute,
  projectEditRoute,
  settingsRoute,
  placementGoalsRoute,
  directPlacementGoalsRoute,
  revenueDataRoute,
  clockLogsRoute,
  clockLogsReportRoute,
  timeManagementRoute,
  workflowTemplatesRoute,
  workflowTemplateCreateRoute,
  workflowTemplateEditRoute,
  workflowInstancesRoute,
  workflowInstanceDetailRoute,
  importsRoute,
  quickBooksConnectionsRoute,
  invoicesRoute,
  invoiceDetailRoute,
  importUploadRoute,
  importRunDetailRoute,
  myWorkflowTasksRoute,
  workflowDashboardRoute,
  placementsRoute,
  documentTemplatesRoute,
  documentTemplateCreateRoute,
  documentTemplateEditRoute,
  workflowEmailTemplatesRoute,
  workflowEmailTemplateCreateRoute,
  workflowEmailTemplateEditRoute,
  workflowLibraryAssetsRoute,
  workflowEmailReviewQueueRoute
} from '@/lib/constants/routes'

Vue.use(Router)

const buildRouteObject = ({ route, component }) => ({
  path: route.path,
  name: route.name,
  component,
  meta: {
    roles: route.roles || null,
    alignTop: !!route.alignTop
  }
})

export default addAuthentication(new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    buildRouteObject({ route: forgotPasswordRoute, component: ForgotPassword }),
    buildRouteObject({ route: resetPasswordRoute, component: ResetPassword }),
    buildRouteObject({ route: loginRoute, component: Login }),
    buildRouteObject({ route: logoutRoute, component: Logout }),
    buildRouteObject({ route: notAuthorizedRoute, component: NotAuthorized }),
    buildRouteObject({ route: defaultRoute, component: Home }),
    buildRouteObject({ route: usersRoute, component: UsersList }),
    buildRouteObject({ route: userCreateRoute, component: UserEdit }),
    buildRouteObject({ route: userEditRoute, component: UserEdit }),
    buildRouteObject({ route: staffRoute, component: StaffList }),
    buildRouteObject({ route: staffCreateRoute, component: StaffEdit }),
    buildRouteObject({ route: staffEditRoute, component: StaffEdit }),
    buildRouteObject({ route: clientsRoute, component: ClientsList }),
    buildRouteObject({ route: clientCreateRoute, component: ClientEdit }),
    buildRouteObject({ route: clientEditRoute, component: ClientEdit }),
    buildRouteObject({ route: businessEntitiesRoute, component: BusinessEntitiesList }),
    buildRouteObject({ route: businessEntityCreateRoute, component: BusinessEntityEdit }),
    buildRouteObject({ route: businessEntityEditRoute, component: BusinessEntityEdit }),
    buildRouteObject({ route: assignmentsRoute, component: AssignmentsList }),
    buildRouteObject({ route: assignmentCreateRoute, component: AssignmentEdit }),
    buildRouteObject({ route: assignmentEditRoute, component: AssignmentEdit }),
    buildRouteObject({ route: consultantTimeSheetRoute, component: ConsultantTimeSheet }),
    buildRouteObject({ route: timeCardReportRoute, component: TimeCardReport }),
    buildRouteObject({ route: myProfileRoute, component: MyProfile }),
    buildRouteObject({ route: directPlacementsRoute, component: DirectPlacementList }),
    buildRouteObject({ route: directPlacementCreateRoute, component: DirectPlacementEdit }),
    buildRouteObject({ route: directPlacementEditRoute, component: DirectPlacementEdit }),
    buildRouteObject({ route: commissionReportRoute, component: CommissionReport }),
    buildRouteObject({ route: commissionDetailsReportRoute, component: CommissionDetailsReport }),
    buildRouteObject({ route: invoiceReportRoute, component: InvoicingReport }),
    buildRouteObject({ route: payrollReportRoute, component: PayrollReport }),
    buildRouteObject({ route: gpToCostReportRoute, component: GPToCostReport }),
    buildRouteObject({ route: dashboardRoute, component: AdminDashboard }),
    buildRouteObject({ route: dealsRoute, component: DealsList }),
    buildRouteObject({ route: dealWizardRoute, component: DealWizardContainer }),
    buildRouteObject({ route: teamsRoute, component: TeamsList }),
    buildRouteObject({ route: projectsRoute, component: ProjectsList }),
    buildRouteObject({ route: projectCreateRoute, component: ProjectEdit }),
    buildRouteObject({ route: projectEditRoute, component: ProjectEdit }),
    buildRouteObject({ route: settingsRoute, component: Settings }),
    buildRouteObject({ route: placementGoalsRoute, component: PlacementGoals }),
    buildRouteObject({ route: directPlacementGoalsRoute, component: DirectPlacementGoals }),
    buildRouteObject({ route: revenueDataRoute, component: RevenueData }),
    buildRouteObject({ route: clockLogsRoute, component: ClockLogs }),
    buildRouteObject({ route: clockLogsReportRoute, component: ClockLogsReport }),
    buildRouteObject({ route: timeManagementRoute, component: TimeManagement }),
    buildRouteObject({ route: workflowTemplatesRoute, component: WorkflowTemplateList }),
    buildRouteObject({ route: workflowTemplateCreateRoute, component: WorkflowTemplateEdit }),
    buildRouteObject({ route: workflowTemplateEditRoute, component: WorkflowTemplateEdit }),
    buildRouteObject({ route: workflowInstancesRoute, component: WorkflowInstanceList }),
    buildRouteObject({ route: workflowInstanceDetailRoute, component: WorkflowInstanceDetail }),
    buildRouteObject({ route: importsRoute, component: ImportRunsList }),
    buildRouteObject({ route: quickBooksConnectionsRoute, component: QuickBooksConnections }),
    buildRouteObject({ route: importUploadRoute, component: ImportUpload }),
    buildRouteObject({ route: importRunDetailRoute, component: ImportRunDetail }),
    buildRouteObject({ route: invoicesRoute, component: InvoicesList }),
    buildRouteObject({ route: invoiceDetailRoute, component: InvoiceDetail }),
    buildRouteObject({ route: myWorkflowTasksRoute, component: MyWorkflowTasks }),
    buildRouteObject({ route: workflowDashboardRoute, component: WorkflowDashboard }),
    buildRouteObject({ route: placementsRoute, component: PlacementsList }),
    buildRouteObject({ route: documentTemplatesRoute, component: DocumentTemplateList }),
    buildRouteObject({ route: documentTemplateCreateRoute, component: DocumentTemplateEdit }),
    buildRouteObject({ route: documentTemplateEditRoute, component: DocumentTemplateEdit }),
    buildRouteObject({ route: workflowEmailTemplatesRoute, component: WorkflowEmailTemplateList }),
    buildRouteObject({ route: workflowEmailTemplateCreateRoute, component: WorkflowEmailTemplateEdit }),
    buildRouteObject({ route: workflowEmailTemplateEditRoute, component: WorkflowEmailTemplateEdit }),
    buildRouteObject({ route: workflowLibraryAssetsRoute, component: WorkflowLibraryAssetList }),
    buildRouteObject({ route: workflowEmailReviewQueueRoute, component: WorkflowEmailReviewQueue })
  ]
}))
